import React, { useState, useEffect } from 'react';
import { Lock, Search, Mail, Copy, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPasswordManagement = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [resettingId, setResettingId] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetch(`${API}/api/users`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => r.json()).then(d => setUsers(d.users || d || [])).catch(() => {});
  }, []);

  const genPassword = (name) => {
    const first = (name || 'User').split(' ')[0];
    return first.charAt(0).toUpperCase() + first.slice(1) + '123';
  };

  const resetPassword = async (user, sendEmail) => {
    const pw = newPassword || genPassword(user.full_name);
    try {
      const r = await fetch(`${API}/api/admin/reset-user-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ user_id: user.id, email: user.email, new_password: pw, send_email: sendEmail }),
      });
      const data = await r.json();
      if (r.ok) {
        if (sendEmail && data.email_sent === false) toast.error(data.message); else toast.success(data.message);
        setResettingId(null);
        setNewPassword('');
      } else {
        toast.error(data.detail || 'Failed');
      }
    } catch { toast.error('Error'); }
  };

  const filtered = users.filter(u => {
    const s = searchTerm.toLowerCase();
    return !s || (u.full_name || '').toLowerCase().includes(s) || (u.email || '').toLowerCase().includes(s);
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Password Management</h1>
        <p className="text-sm text-slate-500 mt-0.5">Reset passwords and email them to employees</p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-500" />
        <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search by name or email..."
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-900 text-sm placeholder-slate-400 outline-none" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-200">
        {filtered.slice(0, 30).map(u => (
          <div key={u.id} className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm text-slate-900 font-medium">{u.full_name}</span>
                <span className="text-xs text-slate-500 ml-2">{u.email}</span>
                <span className="text-[10px] text-slate-600 ml-2 bg-slate-100 px-1.5 py-0.5 rounded">{u.role}</span>
              </div>
              {resettingId === u.id ? (
                <div className="flex items-center gap-2">
                  <input type="text" value={newPassword} onFocus={e => e.target.select()}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder={genPassword(u.full_name)}
                    className="w-32 px-2 py-1.5 bg-white border border-slate-300 rounded text-slate-900 text-xs outline-none" autoFocus />
                  <button onClick={() => resetPassword(u, false)} title="Reset only"
                    className="px-2 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs flex items-center gap-1">
                    <Lock className="w-3 h-3" /> Reset
                  </button>
                  <button onClick={() => resetPassword(u, true)} title="Reset and email"
                    className="px-2 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Reset & Email
                  </button>
                  <button onClick={() => { setResettingId(null); setNewPassword(''); }} className="text-xs text-slate-500">X</button>
                </div>
              ) : (
                <button onClick={() => { setResettingId(u.id); setNewPassword(''); }}
                  className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-xs flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" /> Reset Password
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminPasswordManagement;
